import { InspectorContextValues } from "@app-context/inspector/context";
import { useLocalStorage, useRefCallback } from "@enfusion-ui/hooks";
import {
  DashboardRoot,
  FileSortOption,
  FileTreeEntry,
} from "@enfusion-ui/types";
import { FilterDateType } from "@enfusion-ui/web-components";
import { errorToast, successToast } from "@enfusion-ui/web-core";
import { format, parseISO } from "date-fns";
import { noop, omit } from "lodash";
import React from "react";
import { v4 as uuidv4 } from "uuid";

export type DateFilter = {
  dateType: FilterDateType;
  dateRange: [Date | null, Date | null];
};

export type NodeData = {
  id: string;
  name: string;
  path: string;
  file?: boolean;
  nodes?: Array<NodeData>;
  depth?: number;
  defaultOpen?: boolean;
  custodian?: string;
  config?: InspectorContextValues;
  [key: string]: unknown;
};

export const arrangeNodes = (nodes: NodeData[]) =>
  nodes.sort((a) => (a.file ? 1 : -1));

const filterNodesReduce =
  (searchTerm: string, dateFilter: DateFilter | null) =>
  (res: Array<NodeData>, node: NodeData) => {
    if (node.nodes?.length) {
      const subNodes = node.nodes.reduce(
        filterNodesReduce(searchTerm, dateFilter),
        []
      );
      if (subNodes.length) {
        res.push({ ...node, nodes: subNodes });
      }
    } else if (node.name.toLocaleLowerCase().includes(searchTerm)) {
      if (dateFilter) {
        const storageFileTree = node as unknown as FileTreeEntry;

        const nodeDateTime =
          dateFilter.dateType === "ModifiedAt"
            ? storageFileTree.modifiedDateTime
            : storageFileTree.creationDateTime;

        if (nodeDateTime) {
          const [start, end] = dateFilter.dateRange;
          const formattedNodeDate = format(parseISO(nodeDateTime), "yyyyMMdd");
          if (
            (!start || formattedNodeDate >= format(start, "yyyyMMdd")) &&
            (!end || formattedNodeDate <= format(end, "yyyyMMdd"))
          ) {
            res.push(node);
          }
        }
      } else {
        res.push(node);
      }
    }

    return res;
  };

export type Params = {
  sortOption: FileSortOption;
  query?: string;
  ascending: boolean;
  dateFilter: DateFilter | null;
  fromDate?: Date | null;
  toDate?: Date | null;
};

export const useLocalFiles = () => {
  const [params, setParams] = React.useState<Params>();
  const [savedRequests, setSavedRequests] = useLocalStorage<
    Record<string, NodeData>
  >("saved-requests", {});

  const files = React.useMemo(() => {
    return arrangeNodes(Object.values(savedRequests));
  }, [savedRequests]);

  const createFolder = useRefCallback(
    async (
      folderName: string,
      folderPath: string,
      rootOverride?: DashboardRoot,
      showToast?: boolean
    ) => {
      try {
        const id = uuidv4();
        if (!savedRequests[folderName]) {
          const nodeData: NodeData = {
            id,
            path: `${folderPath}${folderName}`,
            name: folderName,
            file: false,
            level: 0,
            nodes: [],
          };
          setSavedRequests((p) => ({ ...p, [folderName]: nodeData }));
          if (showToast) successToast("Folder created successfully");
        } else {
          errorToast("Folder already exists.");
        }
      } catch (error) {
        errorToast("Failed to create folder");
      }
    },
    [savedRequests, setSavedRequests]
  );

  const moveFile = useRefCallback(
    async (node: NodeData, destinationFolder: NodeData) => {
      try {
        const storedDestinationFolder: NodeData =
          savedRequests[destinationFolder.name];

        const previousNodes: NodeData[] = storedDestinationFolder.nodes || [];
        const existingRequest = previousNodes.filter(
          (element) => element.name === node.name
        );

        if (existingRequest.length === 0) {
          const newFile: NodeData = {
            id: uuidv4(),
            path: `${storedDestinationFolder.path}/${node.name}`,
            name: node.name,
            config: node.config,
            file: true,
          };

          setSavedRequests((p) => ({
            ...p,
            [storedDestinationFolder.name]: {
              ...storedDestinationFolder,
              nodes: [...previousNodes, newFile],
            },
          }));

          successToast("File moved successfully.");
        } else {
          errorToast("File already exists in the specified folder.");
        }
      } catch (err) {
        console.warn("Failed to move file", err);
        errorToast("Failed to move file", (err as Error).message);
      }
    },
    [savedRequests, setSavedRequests]
  );

  const deleteFile = useRefCallback(
    async (node: NodeData, showToast?: boolean) => {
      try {
        if (node.depth === 0) {
          setSavedRequests((p) => omit(p, node.name));
        } else {
          const parentPath = node.path.slice(1, node.path.lastIndexOf("/"));
          setSavedRequests((p) => ({
            ...p,
            [parentPath]: {
              ...p[parentPath],
              nodes: p[parentPath]?.nodes?.filter(
                (x: NodeData) => x.name !== node.name
              ),
            },
          }));
        }
        if (showToast) successToast("File deleted successfully");
      } catch (err) {
        console.warn("Error occurred while deleting the file", err);
        errorToast(
          "Error occurred while deleting the file",
          (err as Error).message
        );
      }
    },
    [savedRequests]
  );

  const deleteFolder = useRefCallback(
    (folder: NodeData, showToast?: boolean) => {
      try {
        setSavedRequests((p) => omit(p, folder.name));
        if (showToast) successToast("Folder deleted successfully");
      } catch (error) {
        errorToast("Error occurred while deleting the folder");
      }
    },
    []
  );

  const deleteAll = useRefCallback((showToast?: boolean) => {
    try {
      setSavedRequests({});
      if (showToast) successToast("Deleted all files successfully");
    } catch (error) {
      errorToast("Error occurred while deleting all the files and folders.");
    }
  }, []);

  const filteredNodes = React.useMemo(() => {
    if (files === null) return [];
    if (params?.query)
      return files.reduce(
        filterNodesReduce(
          params?.query?.trim().toLocaleLowerCase(),
          params?.dateFilter
        ),
        []
      ) as Array<NodeData>;
    return files;
  }, [params?.query, params?.dateFilter, files, savedRequests]);

  return {
    files,
    filteredNodes,
    savedRequests,
    setSavedRequests,
    error: "",
    loading: false,
    deleteFolder,
    deleteFile,
    moveFile,
    createFolder,
    retrieveNodes: (_path: string | NodeData) =>
      Promise.resolve([] as NodeData[]),
    uploadFiles: noop,
    checkForDuplicates: noop,
    setParams,
    deleteAll,
  };
};
